import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useStore, StoreTypes } from "context";
import * as actionTypes from "constants/actionTypes";
import { useLogin } from "util/login";
import { validateClubToken, redirectToClubLogin } from "util/naniClub";
import { getMainPage } from "util/getMainPage";
import styles from "./index.module.scss";

const STATUS = {
  LOADING: "loading",
  ERROR: "error",
};

// 南一會員中心 SSO 登入回調頁（Redirect URI）
// 使用者於會員中心完成登入後被導回此頁，此時 JWT 已由會員中心種在
// .nani.com.tw 網域的 cookie，這裡負責：驗證 → 還原登入狀態 → 導回原頁面。
const NaniClubRedirect = () => {
  const history = useHistory();
  const { getToken, getUser, logoutNaniOneClass } = useLogin();
  const [, userDispatch] = useStore(StoreTypes.user);
  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      // 1. 會員中心登入成功後已將 JWT 種在 .nani.com.tw cookie，直接讀取
      const token = getToken();
      if (!token) {
        if (!cancelled) {
          setErrorMessage("登入失敗：未取得會員中心憑證，請重新登入。");
          setStatus(STATUS.ERROR);
        }
        return;
      }

      // 2. 由會員中心進行完整的伺服器端驗證
      const validation = await validateClubToken(token);
      if (cancelled) return;
      if (!validation.valid) {
        if (validation.unavailable) {
          // validate 服務暫時不可用：Cookie-based SSO 仍可直接使用 cookie 中的
          // JWT，故降級繼續往下走，避免服務中斷。
        } else {
          // 401：Token 確實無效或已過期 → 清除並靜默重導回會員中心重新登入
          logoutNaniOneClass();
          redirectToClubLogin();
          return;
        }
      }

      // 3. 以 JWT 取得用戶資料，還原登入狀態（沿用現有 user store 結構）
      try {
        const source = process.env.REACT_APP_PROJECT.includes("yunlin")
          ? "yunlin"
          : "general";
        const response = await getUser({
          token,
          getExpiredProduct: true,
          source,
        });
        if (cancelled) return;
        userDispatch({
          type: actionTypes.SET_USER_INFO,
          userId: response.username,
          token,
          name: response.name,
          login: true,
          isLogin: true,
          product: response.product,
          isOneLiveMember: response.isOneLiveMember
            ? response.isOneLiveMember
            : false,
          oneLiveMemberCloseTime: response.oneLiveMemberCloseTime
            ? response.oneLiveMemberCloseTime
            : null,
        });
      } catch (err) {
        if (!cancelled) {
          setErrorMessage("取得個人資料失敗，請稍後再試。");
          setStatus(STATUS.ERROR);
        }
        return;
      }

      // 4. 導回原本要去的頁面（redirect cookie），否則回主頁
      const redirect = getCookie("redirect");
      if (redirect) {
        document.cookie =
          "redirect=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;";
        history.replace(redirect);
      } else {
        history.replace(getMainPage());
      }
    };

    handleCallback();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      {status === STATUS.LOADING && (
        <div className={styles.content}>
          <div className={styles.spinner} />
          <p className={styles.message}>登入中，請稍候…</p>
        </div>
      )}
      {status === STATUS.ERROR && (
        <div className={styles.content}>
          <p className={styles.message}>{errorMessage}</p>
          <button
            className={styles.button}
            onClick={() => redirectToClubLogin()}
          >
            重新登入
          </button>
          <button
            className={styles.buttonSecondary}
            onClick={() => history.replace(getMainPage())}
          >
            回首頁
          </button>
        </div>
      )}
    </div>
  );
};

export default NaniClubRedirect;
